import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';
import { CardMenuItemBtn, CardMenuPanel, CardMenuWrap } from './ui.styles';

export default function CardMenu({ items = [] }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const visibleItems = items.filter(Boolean);

  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const menuWidth = 200;
    const gap = 6;
    let left = rect.right - menuWidth;
    if (left < 8) left = 8;
    if (left + menuWidth > window.innerWidth - 8) {
      left = window.innerWidth - menuWidth - 8;
    }
    let top = rect.bottom + gap;
    const estimatedHeight = visibleItems.length * 40 + 8;
    if (top + estimatedHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - estimatedHeight - gap);
    }
    setCoords({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const onScroll = () => updatePosition();
    window.addEventListener('resize', onScroll);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('scroll', onScroll, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, visibleItems.length]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = e => {
      if (wrapRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const handleKey = e => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  if (visibleItems.length === 0) return null;

  const menu = open
    ? createPortal(
        <CardMenuPanel
          ref={menuRef}
          style={{ top: coords.top, left: coords.left }}
          role="menu">
          {visibleItems.map(({ label, icon: Icon, onClick, danger }) => (
            <CardMenuItemBtn
              key={label}
              type="button"
              role="menuitem"
              $danger={danger}
              onClick={e => {
                e.stopPropagation();
                setOpen(false);
                onClick?.();
              }}>
              {Icon ? <Icon size={14} /> : null}
              <span>{label}</span>
            </CardMenuItemBtn>
          ))}
        </CardMenuPanel>,
        document.body,
      )
    : null;

  return (
    <CardMenuWrap ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`trigger${open ? ' open' : ''}`}
        aria-label="Card actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={e => {
          e.stopPropagation();
          setOpen(o => !o);
        }}>
        <MoreVertical size={16} strokeWidth={2.25} />
      </button>
      {menu}
    </CardMenuWrap>
  );
}
